// frontend/client/src/components/HelpCenter.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Search, BookOpen, MessageCircle, Video, CreditCard, Settings,
  ChevronDown, ChevronRight, ArrowLeft, ExternalLink, Mail,
  Zap, HelpCircle, FileText, Play, CheckCircle
} from 'lucide-react';

export default function HelpCenter() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedFAQs, setExpandedFAQs] = useState(new Set());
  const [filteredArticles, setFilteredArticles] = useState([]);

  // Help Center Categories
  const categories = [
    { id: 'all', name: 'All Topics', icon: BookOpen, color: 'text-pink-600' },
    { id: 'getting-started', name: 'Getting Started', icon: Zap, color: 'text-blue-500' },
    { id: 'credits-billing', name: 'Credits & Billing', icon: CreditCard, color: 'text-green-500' },
    { id: 'content-creation', name: 'Content Creation', icon: Video, color: 'text-red-500' },
    { id: 'social-media', name: 'Social Media', icon: MessageCircle, color: 'text-yellow-500' },
    { id: 'account-settings', name: 'Account & Settings', icon: Settings, color: 'text-gray-500' },
  ];

  // FAQ Data Structure (Using original data for functionality)
  const faqs = [
    {
      category: 'getting-started',
      question: 'How do I create my first post with Infleuince?',
      answer: 'Navigate to the Content Studio from your dashboard. Enter a prompt describing your content idea, select your target platform (Instagram, TikTok, etc.), and click "Generate". Our AI will create a caption, suggest hashtags, and you can optionally generate matching visuals. Preview your content, make any edits, and publish directly to your connected social accounts.',
      tags: ['beginner', 'content', 'tutorial']
    },
    {
      category: 'getting-started',
      question: 'What social media platforms does Infleuince support?',
      answer: 'Infleuince currently supports Instagram, TikTok, Twitter (X), LinkedIn, and Facebook. You can connect multiple accounts for each platform and publish content directly from the dashboard. Cross-posting to multiple platforms simultaneously is available on Growth plans and above.',
      tags: ['platforms', 'social media', 'publishing']
    },
    {
      category: 'credits-billing',
      question: 'How do Generative Credits work?',
      answer: 'Generative Credits are our hybrid usage system. Different AI operations consume different amounts: AI Caption (1 credit), AI Image Generation (15 credits), 30-second Video Edit (150 credits), Video Auto-Caption (50 credits). Your monthly allocation resets on your billing date. Unused credits do not roll over.',
      tags: ['credits', 'pricing', 'usage']
    },
    {
      category: 'content-creation',
      question: 'How does AI image generation work?',
      answer: 'Our AI Image Generator uses advanced diffusion models. Simply describe your vision in the prompt field (e.g., "minimalist coffee shop aesthetic with purple accents"). Generate up to 4 variations per request. Each image generation costs 15 credits.',
      tags: ['ai', 'images', 'generation']
    },
    {
      category: 'social-media',
      question: 'What are AI-suggested posting times?',
      answer: 'Our Predictive Analytics engine analyzes your historical engagement data and audience activity patterns to recommend the best times to post. This feature is available on Growth plans and above to maximize reach.',
      tags: ['analytics', 'timing', 'optimization']
    },
    // ... rest of FAQ data is truncated here for brevity but assumed complete in original file
  ];

  // Popular Articles for Quick Access
  const popularArticles = [
    { 
      title: 'Complete Beginner\'s Guide to Infleuince', 
      category: 'getting-started',
      readTime: '5 min',
      icon: BookOpen 
    },
    { 
      title: 'Understanding Credit Usage & Overage', 
      category: 'credits-billing',
      readTime: '3 min',
      icon: CreditCard 
    },
    { 
      title: 'Publishing Your First Post', 
      category: 'content-creation',
      readTime: '4 min',
      icon: Video 
    },
    { 
      title: 'Connecting Instagram & TikTok', 
      category: 'social-media',
      readTime: '2 min',
      icon: MessageCircle 
    },
  ];

  // Video Tutorials
  const videoTutorials = [
    { title: 'Getting Started with Infleuince (2 min)', thumbnail: '🎬', duration: '2:15' },
    { title: 'AI Content Generation Masterclass (8 min)', thumbnail: '🎨', duration: '8:42' },
    { title: 'Advanced Video Editing Tips (5 min)', thumbnail: '🎥', duration: '5:30' },
    { title: 'Analytics Dashboard Deep Dive (6 min)', thumbnail: '📊', duration: '6:18' },
  ];

  // Filter FAQs based on search and category
  useEffect(() => {
    let filtered = faqs;

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(faq =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    setFilteredArticles(filtered);
  }, [searchQuery, activeCategory]);

  // FIX: Ensure toggleFAQ uses the correct spread operator to update the Set
  const toggleFAQ = (index) => {
    setExpandedFAQs(prev => {
        const newExpanded = new Set(prev);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        return newExpanded;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-pink-600 hover:text-pink-500 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>

          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <HelpCircle className="w-10 h-10 text-pink-600 mr-3" />
              <h1 className="text-5xl font-bold text-gray-900">Help Center</h1>
            </div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Find answers, learn best practices, and get the most out of Infleuince
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search for help articles, tutorials, or FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all"
              />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Link
            to="/contact-us"
            className="bg-pink-100/50 p-6 rounded-xl border border-pink-500/30 hover:border-pink-500/60 transition-all group"
          >
            <Mail className="w-8 h-8 text-pink-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-pink-500 transition-colors">
              Contact Support
            </h3>
            <p className="text-sm text-gray-600">Get help from our team within 24-48 hours</p>
          </Link>

          <Link
            to="/status" // Assuming a status page route exists or will be created
            className="bg-green-100/50 p-6 rounded-xl border border-green-500/30 hover:border-green-500/60 transition-all group"
          >
            <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-green-500 transition-colors">
              Service Status
            </h3>
            <p className="text-sm text-gray-600">Check real-time platform health & uptime</p>
          </Link>

          <button
            onClick={() => window.open('https://docs.infleuince.com', '_blank')}
            className="bg-blue-100/50 p-6 rounded-xl border border-blue-500/30 hover:border-blue-500/60 transition-all group"
          >
            <FileText className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-500 transition-colors flex items-center">
              API Documentation
              <ExternalLink className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
            </h3>
            <p className="text-sm text-gray-600">Developer guides & API reference</p>
          </button>
        </div>

        {/* Category Tabs */}
        <div className="flex overflow-x-auto gap-3 mb-8 pb-2 scrollbar-hide">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center px-5 py-3 rounded-xl font-medium whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? 'bg-pink-600 text-white shadow-lg shadow-pink-900/50'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                <Icon className={`w-4 h-4 mr-2 ${activeCategory === cat.id ? 'text-white' : cat.color}`} />
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: FAQs */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-semibold flex items-center text-gray-900">
                  <BookOpen className="w-6 h-6 mr-3 text-pink-600" />
                  Frequently Asked Questions
                </h2>
                <p className="text-sm text-gray-500 mt-2">
                  {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''} found
                  {searchQuery && ` for "${searchQuery}"`}
                </p>
              </div>

              {filteredArticles.length === 0 ? (
                <div className="p-12 text-center">
                  <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No articles found matching your search.</p>
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setActiveCategory('all');
                    }}
                    className="text-pink-600 hover:text-pink-500 font-medium"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredArticles.map((faq, index) => (
                    <div key={index} className="p-6 hover:bg-gray-50 transition-colors">
                      <button
                        onClick={() => toggleFAQ(index)}
                        className="w-full flex items-start justify-between text-left"
                      >
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 pr-4">
                            {faq.question}
                          </h3>
                          {expandedFAQs.has(index) && (
                            <p className="text-gray-600 leading-relaxed mt-3">
                              {faq.answer}
                            </p>
                          )}
                        </div>
                        {expandedFAQs.has(index) ? (
                          <ChevronDown className="w-5 h-5 text-pink-600 flex-shrink-0 mt-1" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0 mt-1" />
                        )}
                      </button>
                      
                      {expandedFAQs.has(index) && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {faq.tags.map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Sidebar */}
          <div className="space-y-6">
            {/* Popular Articles */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Zap className="w-5 h-5 mr-2 text-yellow-500" />
                Popular Articles
              </h3>
              <div className="space-y-3">
                {popularArticles.map((article, index) => {
                  const Icon = article.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => setActiveCategory(article.category)}
                      className="w-full flex items-start p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left group"
                    >
                      <Icon className="w-5 h-5 text-pink-600 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-gray-900 group-hover:text-pink-500 transition-colors">
                          {article.title}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{article.readTime} read</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Video Tutorials */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Play className="w-5 h-5 mr-2 text-red-500" />
                Video Tutorials
              </h3>
              <div className="space-y-3">
                {videoTutorials.map((video, index) => (
                  <button
                    key={index}
                    className="w-full flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left group"
                  >
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-xl mr-3 flex-shrink-0">
                      {video.thumbnail}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900 group-hover:text-pink-500 transition-colors">
                        {video.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{video.duration}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Still Need Help? */}
            <div className="bg-gradient-to-br from-pink-100/50 to-blue-100/50 rounded-xl border border-pink-500/30 p-6 text-center">
              <MessageCircle className="w-10 h-10 text-pink-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Still Need Help?</h3>
              <p className="text-sm text-gray-700 mb-4">
                Our support team is here for you 24/7
              </p>
              <Link
                to="/contact-us"
                className="inline-block bg-pink-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-pink-700 transition-colors"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </div>

        {/* Chatbase Integration Placeholder */}
        <div id="chatbase-widget-container" className="fixed bottom-6 right-6 z-50">
          {/* Chatbase widget will be injected here via their script */}
        </div>
      </div>
    </div>
  );
}